// Модальные окна: карта крупно, очки, жетоны, стопки, журнал, меню, ручные правки, памятка.
// Preact-компоненты; наружу — прежние строковые функции (renderToString), пока app.ts рендерит через innerHTML.
// Detail-вид карты один — CardDetail (зум в партии и справка вне партии отличаются только действиями и сносками).
import { h, Fragment } from 'preact';
import type { ComponentChildren, JSX } from 'preact';
import { render as toString } from 'preact-render-to-string';
import type { AccountInfo, BossId, BossStat, BotLevel, RatingLevel, CardRef, Game as GameState, PlayedGame, Player, PlayerId, PlayerProfile, CardId, CardInst } from '../types.ts';
import { DB, card, maxLife, player, traitOf, turnOf, ddkPenalty, baseCost, baseText, deckKnown, handKnown } from '../engine/state.ts';
import { REBALANCE, REBALANCE_NOTES, REBALANCE_TRAIT_NOTES } from '../engine/rebalance.ts';
import { canActivate, chipsPayable, H, CHAOS_CARD } from '../engine/game.ts';
import { RULES } from '../engine/rules.ts';
import { mutatorDefs, MUTATORS, MUTATOR_IDS } from '../engine/mutators.ts';
import { BOSSES, BOSS_IDS } from '../engine/bosses.ts';
import { esc, typeLabel, cardV, CardChipV, Txt, ChipIc, isRebalanced } from './cards.ts';
import { cancellable, undoWhat, undoBlocked } from './board.ts';
import { cnt, handCount, affordInfo, displayScore, scoreOf } from './board.ts';
import { allItems } from './events.ts';
import type { ReviewItem } from '../engine/review.ts';
import { feedItemHtml, MEDAL, gamesWord, winsWord } from './screens.ts';
import { LEVEL_INFO, STYLES } from '../engine/bot.ts';
import { isMuted } from './sound.ts';
import { IOS_HINT, CHROME_HINT, chromeIntentUrl } from './install.ts';
import { CHANGELOG, fmtDate } from './changelog.ts';
import type { InboxItem, SysPermission } from './inbox.ts';
import type { PushState } from './store.ts';
import { cmdProps } from './commands.ts';

import { Raw, Btn, Close, Actions, Hint, Sub, cx } from './ui-kit.ts';
import type { V } from './ui-kit.ts';
import { AchSectionV } from './ach.ts';

/** Обёртка окна: клик по затемнению закрывает */
function overlayV(children: ComponentChildren, cls = '', dialogCls = ''): JSX.Element {
  return h('div', { class: `overlay ${cls}`.trim(), 'data-overlay': '1' }, h('div', { class: `dialog ${dialogCls}`.trim() }, children));
}

/** Сетка мини-карт (стопки, постоянки, сбросы) */
const Grid = ({ cards, keyPrefix, empty = 'пусто' }: { cards: readonly CardInst[]; keyPrefix: string; empty?: string }): V =>
  h('div', { class: 'grid' }, cards.length ? cards.map((c) => cardV({ inst: c }, { cls: 'sm', key: `${keyPrefix}:${c.iid}`, zone: 'view' })) : h(Hint, null, empty));
const byCostDesc = (cards: readonly CardInst[]): CardInst[] =>
  [...cards].sort((a, b) => (card(b).cost ?? 0) - (card(a).cost ?? 0) || card(a).name.localeCompare(card(b).name));

// ---------- крупный вид карты (общий для зума в партии и справки вне партии) ----------
/** Откуда открыта карта: определяет доступные действия */
export type ZoomKind = 'hand' | 'market' | 'chaos' | 'inplay' | 'view';

export interface ZoomOpts {
  /** мой ход и нет запроса — действия доступны */
  isActive: boolean;
  /** чипсины, выбранные для оплаты легенды */
  useChips: number;
  /** показан скан оригинала (кнопка «Оригинал») */
  orig?: boolean;
}

interface DetailProps {
  cid: CardId;
  /** цена и текст, которые ДЕЙСТВУЮТ в показываемом контексте (в партии — из DB, в справке режима — правленые) */
  cost: number | null;
  text: string;
  /** карта правлена для этого контекста: показать блок «в обычных правилах» с исходными ценой и текстом */
  patched?: boolean;
  /** дополнение к строке типа («в стопке: N») */
  subExtra?: string;
}

/**
 * Тело крупного вида: арт, тип/стоимость/ПО, имя, текст, колдун, символ чипсины.
 * Показывает ровно то, что действует сейчас. Если карта правлена — исходные значения идут отдельным блоком
 * «в обычных правилах» (сравнение, а не сноска-опровержение поверх правленых чисел).
 */
function CardDetail({ cid, cost, text, patched, subExtra }: DetailProps): V {
  const d = DB.cards[cid];
  if (!d) return h('h2', null, 'Карта не найдена');
  const sub = [typeLabel(d), cost != null ? `стоимость ${cost}` : '', d.vp != null ? `ПО ${d.vp}` : '', subExtra ?? ''].filter(Boolean).join(' · ');
  const origCost = baseCost(cid);
  const origText = baseText(cid);
  const costChanged = patched && origCost != null && cost != null && origCost !== cost;
  const textChanged = patched && origText !== text;
  return h('div', { class: 'detail' },
    h('img', { src: d.art ?? d.image, alt: '', draggable: false }),
    h('div', { class: 'info' },
      h(Sub, null, patched ? `⚖ ${sub}` : sub),
      h('h2', null, d.name),
      h(Txt, { cls: 'cardtext', text }),
      costChanged || textChanged ? h('div', { class: 'orig-rules' },
        h(Sub, null, 'В обычных правилах:'),
        costChanged ? h(Sub, null, `стоимость ${origCost}`) : null,
        textChanged ? h(Txt, { cls: 'cardtext dim', text: origText }) : null) : null,
      d.familiarOf.length ? h(Sub, { cls: 'sub mt' }, `Колдун: ${d.familiarOf.join(' / ')}`) : null,
      d.chipsina ? h(Sub, null, h(ChipIc, null), ' Символ чипсины: при выкладывании на барахолку на карту кладётся 1 чипсина') : null));
}

/** Скан оригинальной карты (кнопка «Оригинал» переключает) */
const OrigBox = ({ cid, shown = false }: { cid: CardId; shown?: boolean }): V =>
  h('div', { id: 'origbox', hidden: !shown, class: 'origbox' }, h('img', { src: DB.cards[cid]?.image ?? '', alt: '', draggable: false }));
const OrigBtn = (): V => h(Btn, { cls: 'ghost', c: { k: 'toggleOrig' } }, 'Оригинал');

export function zoomV(G: GameState, c: CardRef, kind: ZoomKind, { isActive, useChips, orig = false }: ZoomOpts): JSX.Element {
  const d = card(c);
  const turn = turnOf(G);
  const active = player(G, turn.player);
  const actions: V[] = [];
  const extra: V[] = [];
  if (kind === 'hand' && isActive) actions.push(h(Btn, { cls: 'primary', c: { k: 'play', iid: c.iid } }, 'Сыграть'));
  if (kind === 'market' && isActive) {
    const { price, chipPay } = affordInfo(G, active, c);
    if (chipPay > 0) {
      extra.push(h('div', { class: 'panel2' },
        h(Hint, null, `Цена ${price}. Чипсинами можно оплатить часть (1 чипсина = 1 мощь).`),
        h('div', { class: 'stepper small' }, h(Btn, { c: { k: 'bc', d: -1 } }, '−'), h('b', null, useChips), h(Btn, { c: { k: 'bc', d: 1 } }, '+')),
        h('div', { class: 'hint center' }, `чипсин: ${useChips}, мощи: ${price - useChips}`)));
    }
    if (d.type === 'фамильяр') extra.push(h(Hint, null, `Фамильяр под планшетом: цена ${RULES.familiarPrice}${chipsPayable(active, c) ? ' — по жетону свойства считается легендой: действует скидка на легенды, можно доплатить чипсинами' : ''}.`));
    actions.push(h(Btn, { cls: 'primary', c: { k: 'buy', iid: c.iid }, disabled: turn.power < price - useChips }, `Купить за ${price}`));
  }
  if (kind === 'chaos' && isActive) {
    const n = cnt(G.chaosPile, G.chaosPileCount);
    actions.push(h(Btn, { cls: 'primary', c: { k: 'buyChaos' }, disabled: !(turn.power >= RULES.chaosPrice && n) }, `Купить за ${RULES.chaosPrice}`));
  }
  if (kind === 'inplay' && isActive && canActivate(G, c)) actions.push(h(Btn, { cls: 'primary', c: { k: 'activate', iid: c.iid } }, 'Применить ☀'));
  // в партии DB уже несёт значения режима: показываем их, а исходные — блоком «в обычных правилах»
  return overlayV(h(Fragment, null,
    h(CardDetail, { cid: d.id, cost: d.cost, text: d.text, patched: isRebalanced(d.id), ...(kind === 'chaos' ? { subExtra: `в стопке: ${cnt(G.chaosPile, G.chaosPileCount)}` } : {}) }),
    extra,
    h(OrigBox, { cid: d.id, shown: orig }),
    h('div', { class: 'actions' }, actions, h(OrigBtn, null), h(Close, null))), 'zoom');
}

/** Полное описание карты с картинкой вне партии (тап по плашке в справке ребаланса на стартовом экране) */
export function cardInfoV(cid: string, orig = false): JSX.Element {
  const d = DB.cards[cid];
  if (!d) return overlayV(h(Fragment, null, h('h2', null, 'Карта не найдена'), h(Actions, null)));
  // справку открывают из режима ребаланса — значит показываем карту такой, какой она будет в партии режима
  const patch = REBALANCE[cid];
  return overlayV(h(Fragment, null,
    h(CardDetail, { cid, cost: patch?.cost ?? baseCost(cid), text: patch?.text ?? baseText(cid), patched: !!patch }),
    h(OrigBox, { cid, shown: orig }),
    h('div', { class: 'actions' }, h(OrigBtn, null), h(Close, null))), 'zoom');
}

/** Карта стопки шальной магии для окна: у стопки нет экземпляра, показываем описание */
export const chaosRef: CardRef = { iid: 0, cid: CHAOS_CARD };

// ---------- остальные окна ----------
export function scoreV(G: GameState, p: Player): JSX.Element {
  const sc = scoreOf(G, p);
  return overlayV(h(Fragment, null,
    h('h2', null, `★ Очки: ${p.name}`),
    h(Hint, null, 'Считается так же, как в конце игры: все карты игрока (колода, рука, сброс, стол) + условные бонусы − вялые палочки − жетоны дохлых колдунов − лошара.'),
    h('table', { class: 'score' },
      sc.lines.map((l) => h('tr', null, h('td', null, l.label), h('td', { class: 'num' }, `${l.value > 0 ? '+' : ''}${l.value}`))),
      h('tr', null, h('td', null, h('b', null, 'Итого')), h('td', { class: 'num' }, h('b', null, sc.total))),
      h('tr', null, h('td', { class: 'sub' }, `легенд: ${sc.legends}, ЖДК: ${sc.ddk}`), h('td', null))),
    h(Actions, null)));
}

/** Жетон-медальон с текстом (свойство или ЖДК) */
const Medal = ({ title, text }: { title?: string; text: string | null | undefined }): V =>
  h('div', { class: 'token token-medal' }, h('div', { class: 'medal-in' },
    title ? h('div', { class: 'medal-title' }, title) : null,
    h(Txt, { cls: 'cardtext', text: text || 'Без текста.' })));

/** Жетон на столе: текст + «Применить ☀», если активируемый и доступен сейчас */
export function tokenCardV(G: GameState, tok: string, canActNow: boolean): JSX.Element {
  const active = player(G, turnOf(G).player);
  const isTrait = tok === 'trait';
  const ddk = isTrait ? null : DB.ddk[tok];
  const text = isTrait ? traitOf(active)?.text : ddk?.text;
  const a = H.tokenActivations(G, active).find((x) => (isTrait ? x.id.startsWith('trait') : x.id === 'ddk-' + (ddk?.key ?? ''))) ?? null;
  const enabled = !!a && a.enabled !== false && canActNow;
  return overlayV(h(Fragment, null,
    h(Medal, { title: isTrait ? 'Жетон колдунского свойства' : 'Жетон дохлого колдуна', text }),
    a && !enabled ? h(Hint, null, a.hint ?? 'Сейчас недоступно') : null,
    h('div', { class: 'actions' }, a && enabled ? h(Btn, { cls: 'primary', c: { k: 'activateToken', id: a.id } }, a.label) : null, h(Close, null))));
}

export function tokensV(me: Player): JSX.Element {
  const t = traitOf(me);
  return overlayV(h(Fragment, null,
    h('h2', null, `Жетоны: ${me.name}`),
    h(Sub, null, `Колдун: ${me.wizard ?? ''}`),
    t ? h(Fragment, null, h('h3', null, 'Жетон колдунского свойства'), h(Medal, { text: t.text })) : null,
    h('h3', null, `Жетоны дохлых колдунов (${me.ddk.length})`),
    me.ddk.length ? null : h(Hint, null, 'Пока ни одного — молодец.'),
    me.ddk.map((id) => {
      const d = DB.ddk[id];
      return d ? h('div', { class: 'token' }, h(Txt, { cls: '', text: d.text || 'Без текста.' }), h(Sub, null, `${ddkPenalty(d)} ПО в конце игры${d.permanent ? ' · постоянка' : ''}`)) : null;
    }),
    me.familiars.length ? h(Fragment, null,
      h('h3', null, `Фамильяры под планшетом (покупка за ${RULES.familiarPrice})`),
      h('div', { class: 'mrow' }, me.familiars.map((c) => cardV({ inst: c }, { cls: 'sm', key: 'tk:' + c.iid, zone: 'view' })))) : null,
    h(Actions, null)));
}

/** Публично известные верх/низ колоды: из представления (сервер) или по полному состоянию (хотсит) */
function Known({ p, keyPrefix }: { p: Player; keyPrefix: string }): V {
  const top = p.knownTopCards ?? deckKnown(p).top;
  const bottom = p.knownBottomCards ?? deckKnown(p).bottom;
  return h(Fragment, null,
    top.length ? h(Fragment, null,
      h('div', { class: 'pick-zone' }, 'Известно сверху ', h('span', { class: 'sub' }, '· первая — самая верхняя')),
      h('div', { class: 'grid' }, top.map((c) => cardV({ inst: c }, { cls: 'sm', key: `${keyPrefix}:t${c.iid}`, zone: 'view' })))) : null,
    bottom.length ? h(Fragment, null,
      h('div', { class: 'pick-zone' }, 'Известно снизу ', h('span', { class: 'sub' }, '· первая — самая нижняя')),
      h('div', { class: 'grid' }, bottom.map((c) => cardV({ inst: c }, { cls: 'sm', key: `${keyPrefix}:b${c.iid}`, zone: 'view' })))) : null);
}

export function pileV(me: Player, kind: 'deck' | 'discard'): JSX.Element {
  const body = kind === 'discard'
    ? h(Fragment, null,
        h('h2', null, `Сброс (${me.discard.length})`),
        h(Hint, null, 'Открытая информация. Когда колода кончается, сброс замешивается.'),
        h(Grid, { cards: me.discard, keyPrefix: 'pile' }))
    : h(Fragment, null,
        h('h2', null, `Колода (${cnt(me.deck, me.deckCount)})`),
        h(Known, { p: me, keyPrefix: 'pk' }),
        h(Hint, null, 'Порядок остальных карт скрыт — только состав (от большей стоимости к меньшей).'),
        h(Grid, { cards: byCostDesc(me.deck), keyPrefix: 'pile' }));
  return overlayV(h(Fragment, null, body, h(Actions, null)));
}

export function logV(G: GameState, me: PlayerId | null): JSX.Element {
  const items = allItems(G, G.events, me);
  // граница «с твоего последнего хода»: первый чужой ход после моего последнего (если сейчас мой ход — после предыдущего),
  // чтобы было видно, что случилось за прошедший круг
  let cut = -1;
  if (me != null) {
    const mine = items.map((d, i) => (d.type === 'turn' && d.pids?.[0] === me ? i : -1)).filter((i) => i >= 0);
    const myTurnNow = G.phase === 'play' && G.turn?.player === me;
    const from = myTurnNow ? mine[mine.length - 2] ?? -1 : mine[mine.length - 1];
    if (from != null) cut = items.findIndex((d, i) => i > from && d.type === 'turn');
  }
  const lines = items.map((d, i) => {
    const sep = i === cut ? '<div class="feed-sep">— с твоего последнего хода —</div>' : '';
    return sep + feedItemHtml(G, d, cut >= 0 && i >= cut ? 'since' : '');
  }).join('');
  const raw = G.log.slice(-200).map((l) => `<div>${esc(l)}</div>`).join('');
  return overlayV(h(Fragment, null,
    h('h2', null, 'Журнал'),
    h(Raw, { cls: 'feed tall', id: 'logbox', html: lines || raw }),
    h('details', { class: 'mt' }, h('summary', { class: 'sub' }, 'Текстовый журнал движка'), h(Raw, { cls: 'log', html: raw })),
    h(Actions, null)));
}

/** Крупнейшие потери одного игрока в разборе; jump — в плеере записи: тап по пункту переносит к этому решению */
function ReviewOf({ name, items, jump = false }: { name: string; items: readonly ReviewItem[]; jump?: boolean }): V {
  const mine = items.slice(0, 5);
  const body = (i: ReviewItem): ComponentChildren[] => [
    h('span', { class: 'sub' }, `ход ${i.turnNo} · ${i.context} · −${i.loss.toFixed(1)}`),
    h('br', null),
    `${i.actualText} → сильнее: `,
    h('b', null, i.betterText),
  ];
  return h('div', { class: 'panel mt' },
    h('b', null, name),
    mine.length ? null : h(Sub, null, 'крупных потерь не найдено — сыграно на уровне бота 👍'),
    mine.map((i) => jump
      ? h(Btn, { cls: 'rv-item mt', title: 'показать этот момент на столе', c: { k: 'replayAt', idx: i.idx } }, body(i), h('span', { class: 'rv-go' }, ' ▶ к этому моменту'))
      : h('div', { class: 'mt' }, body(i))));
}

const REVIEW_SUB = 'Решения, где примерка бота видит потерю (в очках-эквивалентах). Мелкие расхождения — вкус, не ошибка.';

/** Послематчевый разбор: крупнейшие потери каждого человека против хода бота (движковый reviewGame) */
export function reviewV(G: GameState, items: readonly ReviewItem[]): JSX.Element {
  return overlayV(h(Fragment, null,
    h('h2', null, '🔍 Разбор партии'),
    h(Sub, null, REVIEW_SUB),
    G.players.filter((p) => !p.bot).map((hp) => h(ReviewOf, { name: hp.name, items: items.filter((i) => i.pid === hp.id) })),
    h(Actions, null)));
}

/** Как установить: подсказка для iOS Safari или кнопка «Открыть в Chrome» для Android-браузеров без настоящей установки */
export function installHintV(how: 'ios' | 'chrome' = 'ios'): JSX.Element {
  const hint = how === 'chrome' ? CHROME_HINT : IOS_HINT;
  return overlayV(h(Fragment, null,
    h('h2', null, '📲 Установить как приложение'),
    h(Raw, { cls: 'hint', html: hint }),
    h('div', { class: 'actions' },
      how === 'chrome' ? h('a', { class: 'install-btn', href: chromeIntentUrl() }, h('span', { class: 'install-ic' }, '📲'), h('span', null, 'Открыть в Chrome')) : null,
      h(Close, null))));
}

/** Страница открыта кнопкой «Установить через Chrome»: одна кнопка → системный диалог; 'missing' — Chrome установку не предложил */
export function installNowV(state: 'ready' | 'missing'): JSX.Element {
  if (state === 'missing') {
    return overlayV(h(Fragment, null,
      h('h2', null, '📲 Установить как приложение'),
      h(Hint, null, 'Chrome не предлагает установку — скорее всего, игра уже установлена: запусти её значком с рабочего стола. Если значка нет — меню Chrome ⋮ → «Установить приложение».'),
      h(Actions, null)));
  }
  return overlayV(h(Fragment, null,
    h('h2', null, '📲 Установить Крутагидон'),
    h(Hint, null, 'Одно нажатие — и игра появится на рабочем столе как приложение во весь экран.'),
    h('div', { class: 'actions' }, h(Btn, { cls: 'primary', c: { k: 'installNow' } }, 'Установить'), h(Close, null))));
}

/** Установка подтверждена: значок появится на рабочем столе; пока — развернуть игру на весь экран в этой вкладке (нужно касание) */
export function installedV(): JSX.Element {
  return overlayV(h(Fragment, null,
    h('h2', null, '🎉 Крутагидон устанавливается'),
    h('div', { class: 'hint' },
      'Через несколько секунд значок появится на рабочем столе — запускай игру им, она откроется во весь экран без адресной строки.',
      h('br', null), h('br', null),
      'Если на рабочем столе значка нет — он в общем списке приложений (обычно в самом конце): зажми его и перетащи на рабочий стол.',
      h('br', null), h('br', null),
      'А эту вкладку можно развернуть на весь экран прямо сейчас.'),
    h('div', { class: 'actions' }, h(Btn, { cls: 'primary', c: { k: 'installedFs' } }, 'На весь экран'), h(Close, null))));
}

/**
 * Меню партии; install — показать пункт «установить как приложение» (игра открыта в браузере, а не установлена).
 * me — кто смотрит: ему предлагается «шаг назад», если его последнее действие ещё можно отменить.
 * unread — непрочитанных уведомлений (приглашения приходят и посреди партии).
 */
export function menuV(G: GameState, online: boolean | 'corr', install = false, me: PlayerId | null = null, unread = 0): JSX.Element {
  const undo = me != null && cancellable(G, me) ? undoWhat(G, me) : null;
  const blocked = me != null ? undoBlocked(G, me) : null;
  return overlayV(h(Fragment, null,
    h('h2', null, 'Меню'),
    undo != null
      ? h(Btn, { cls: 'primary', id: 'menu-undo', c: { k: 'cancel' } }, `↩ Отменить ${undo}`)
      : me != null && blocked ? h(Hint, { cls: 'hint mb' }, `↩ Отмена недоступна: ${blocked}`) : null,
    h(Btn, { c: { k: 'menu', item: 'log' } }, '📜 Журнал событий'),
    h(Btn, { c: { k: 'menu', item: 'tokens' } }, '🎯 Мои жетоны и фамильяры'),
    h(Btn, { c: { k: 'menu', item: 'trash' } }, `🗑 Уничтоженные карты (${G.trash.length}) и беспределы (${G.trashBespredel.length})`),
    h(Btn, { c: { k: 'menu', item: 'manual' } }, '✎ Ручные правки (жизни/чипсины/мощь)'),
    h(Btn, { c: { k: 'menu', item: 'rules' } }, '📖 Памятка по правилам'),
    h(Btn, { c: { k: 'menu', item: 'mute' } }, isMuted() ? '🔇 Звук выключен — включить' : '🔊 Звук включён — выключить'),
    // на телефоне кнопки ⛶ в шапке нет (две кнопки вплотную — промахи), полный экран включается отсюда; без API / уже во весь экран — скрыт стилем
    h(Btn, { id: 'menu-fs', c: { k: 'menu', item: 'fs' } }, '⛶ На весь экран'),
    h(Btn, { c: { k: 'menu', item: 'inbox' } }, `🔔 Уведомления${unread > 0 ? ` (${unread})` : ''}`),
    h(Btn, { c: { k: 'menu', item: 'changelog' } }, '📋 Что нового'),
    install ? h(Btn, { c: { k: 'menu', item: 'install' } }, '📲 Установить как приложение') : null,
    online === 'corr'
      // партия по переписке без нас не кончится — уйти к списку не страшно
      ? h(Btn, { c: { k: 'menu', item: 'leave' } }, '‹ К партиям по переписке')
      : online
      ? h(Btn, { cls: 'danger', c: { k: 'menu', item: 'leave' } }, 'Выйти из комнаты (можно вернуться по коду)')
      : h(Btn, { cls: 'danger', c: { k: 'menu', item: 'quit' } }, 'Выйти в главное меню (партия сохранится)'),
    h(Actions, null)), '', 'menu');
}

export function manualV(G: GameState): JSX.Element {
  const adj = (pid: PlayerId, field: 'life' | 'chips' | 'loser' | 'draw' | 'power', delta: number, label: ComponentChildren): V =>
    h(Btn, { c: { k: 'adjust', pid, field, delta } }, label);
  return overlayV(h(Fragment, null,
    h('h2', null, 'Ручные правки'),
    h(Hint, null, 'На случай, если эффект карты нужно применить вручную или исправить ошибку.'),
    G.players.map((p) => h('div', { class: 'token' },
      h('b', null, p.name), ` — ❤${p.life} `, h(ChipIc, null), `${p.chips} ${p.loser ? '· лошара' : ''}`,
      h('div', { class: 'row mt' },
        adj(p.id, 'life', -1, '❤−1'), adj(p.id, 'life', 1, '❤+1'),
        adj(p.id, 'chips', -1, h(Fragment, null, h(ChipIc, null), '−1')), adj(p.id, 'chips', 1, h(Fragment, null, h(ChipIc, null), '+1')),
        adj(p.id, 'loser', 0, 'лошара вкл/выкл'), adj(p.id, 'draw', 1, 'взять карту')))),
    h('div', { class: 'token' },
      h('b', null, `Мощь текущего хода: ${G.turn?.power ?? 0}`),
      h('div', { class: 'row mt' }, adj(0, 'power', -1, '⚡−1'), adj(0, 'power', 1, '⚡+1'))),
    h(Actions, null)));
}

export function trashV(G: GameState): JSX.Element {
  return overlayV(h(Fragment, null,
    h('h2', null, `Уничтоженные карты (${G.trash.length})`),
    h(Grid, { cards: G.trash, keyPrefix: 'tr' }),
    h('h2', null, 'Сыгранные беспределы (по порядку)'),
    G.trashBespredel.length
      ? G.trashBespredel.map((c, i) => h('div', { class: 'token' }, `${i + 1}. `, h(Txt, { cls: '', text: card(c).text })))
      : h(Hint, null, 'пусто'),
    h(Actions, null)));
}

const RULES_MEMO = `
<b>Управление:</b> тап по карте руки — поднять и прочитать, перетащи вверх на стол — сыграть, на игрока — сыграть и сразу выбрать его целью. Вкладка «Барахолка»: тап по карте — купить, или перетащи карту вниз к себе.
<b>Ход:</b> барахолки пополняются (беспределы срабатывают сразу) → играй карты с руки в любом порядке, копи мощь ⚡ → покупай карты (они идут в сброс) → «Конец хода»: рука и сыгранные карты в сброс, взять 5.
<b>Простые карты:</b> карты без выбора и без атаки игра разыгрывает за тебя сама — чтобы не щёлкать «мощь, мощь, мощь». Передумал — «↩ Отмена» вернёт их в руку, и дальше играй сам (в этом ходу автоматика их больше не тронет).
<b>Чипсины <span class="cticon">▲</span>:</b> 1 чипсина = 1 мощь только при покупке легенд; также оплата свойств карт. Главный приз 🏆 даёт +1 чипсину в конце хода владельца.
<b>Атака:</b> цель может сыграть 1 карту защиты с руки. «Враг» — любой, кроме тебя; «колдун» — можно и себя.
<b>Смерть:</b> жизни &lt; 1 → жетон дохлого колдуна (−3 ПО + текст), снова 20 жизней; убийца получает приз.
<b>Лошара:</b> макс. 15 жизней, −5 ПО в конце.
<b>Постоянки ∞:</b> остаются в игре. Свойства ☀ — раз в ход, в любой момент своего хода.
<b>Конец игры:</b> не хватает карт пополнить барахолку (основную или легенд) или кончились ЖДК. ПО = карты + бонусы − вялые палочки − ЖДК − лошара.`;

export function rulesV(): JSX.Element {
  return overlayV(h(Fragment, null, h('h2', null, 'Памятка'), h(Raw, { cls: 'cardtext', html: RULES_MEMO }), h(Actions, null)));
}

/**
 * Закрытые карты соперника: колода и рука. Отдельный состав колоды выдал бы руку (всё, чего нет в колоде, сбросе и
 * на столе, — на руке), поэтому они показаны вместе. Что видно и за настоящим столом — показано честно: известные всем
 * карты руки (взяты в открытую, показаны эффектом) отдельно; пустая колода — всё закрытое на руке; пустая рука — всё
 * в колоде; вся рука известна — остальное и есть колода.
 */
function OpponentClosed({ p, n }: { p: Player; n: number }): V {
  const hn = handCount(p);
  const known = p.knownHandCards ?? handKnown(p);
  // полное состояние (хотсит) — колода и рука, онлайн — их общий состав от сервера (карты без iid)
  const closed = p.closedCids ? p.closedCids.map((cid, i) => ({ iid: -1 - i, cid })) : [...p.deck, ...p.hand];
  // закрытое без известных карт руки: в полном состоянии — по iid, в онлайне — по картам (у состава нет iid)
  const rest = [...closed];
  for (const k of known) {
    const i = rest.findIndex((c) => c.iid === k.iid);
    const j = i >= 0 ? i : rest.findIndex((c) => c.iid < 0 && c.cid === k.cid);
    if (j >= 0) rest.splice(j, 1);
  }
  const grid = (cs: readonly CardInst[], key: string): V =>
    cs.length ? h('div', { class: 'grid' }, byCostDesc(cs).map((c) => cardV({ inst: c }, { cls: 'sm', key: `${key}:${c.iid}`, zone: 'view' }))) : h(Hint, null, 'пусто');
  const order = 'только состав (от большей стоимости к меньшей)';
  if (n === 0) {
    return h(Fragment, null,
      h('h3', null, `Рука (${hn}) — колода пуста`),
      hn ? h(Hint, null, 'Колода пуста — значит, все закрытые карты на руке.') : null,
      grid(closed, 'pih'));
  }
  if (hn === 0) {
    return h(Fragment, null,
      h('h3', null, `Колода (${n}) — рука пуста`),
      h(Known, { p, keyPrefix: 'pik' }),
      h(Hint, null, `Рука пуста — всё это в колоде. Порядок карт неизвестен — ${order}.`),
      grid(closed, 'pid'));
  }
  if (known.length >= hn) {
    return h(Fragment, null,
      h('h3', null, `Рука (${hn}) — известна вся`),
      grid(known, 'pih'),
      h('h3', null, `Колода (${n})`),
      h(Known, { p, keyPrefix: 'pik' }),
      h(Hint, null, `Порядок карт неизвестен — ${order}.`),
      grid(rest, 'pid'));
  }
  return h(Fragment, null,
    known.length ? h(Fragment, null, h('h3', null, `Известно на руке (${known.length} из ${hn})`), grid(known, 'pih')) : null,
    h('h3', null, known.length ? `Колода и остальная рука (${n + hn - known.length})` : `Колода и рука (${n + hn})`),
    h(Known, { p, keyPrefix: 'pik' }),
    h(Hint, null, `Колода и рука вместе: какие из этих карт на руке, не видно — ${order}.`),
    grid(rest, 'pid'));
}

export function playerInfoV(G: GameState, p: Player, isMe: boolean): JSX.Element {
  const t = traitOf(p);
  const n = cnt(p.deck, p.deckCount);
  return overlayV(h(Fragment, null,
    h('h2', null, `${p.name}${isMe ? ' (ты)' : ''}`),
    h(Sub, null, h(Fragment, null, `${p.wizard ?? ''} · ❤${p.life}/${maxLife(p)} · `, h(ChipIc, null), `${p.chips} · ★${displayScore(G, p)} · колода ${n}, рука ${handCount(p)}, сброс ${p.discard.length}${G.prizeOwner === p.id ? ' · 🏆 главный приз' : ''}${p.loser ? ' · 🐴 лошара' : ''}`)),
    p.boss ? h(Fragment, null, h('h3', null, '👑 Босс'), h('div', { class: 'boss-card' },
      h('img', { class: 'boss-face', src: DB.cards[BOSSES[p.boss].familiar]?.art ?? '', alt: '' }),
      h('div', null, h('b', null, BOSSES[p.boss].name), h('div', { class: 'cardtext' }, BOSSES[p.boss].text), h(Sub, null, `Фамильяр: ${DB.cards[BOSSES[p.boss].familiar]?.name ?? ''} · ${LEVEL_INFO.hard.emoji} сложный бот, характер ${STYLES[BOSSES[p.boss].style].emoji} ${STYLES[BOSSES[p.boss].style].label}`)))) : null,
    t ? h(Fragment, null, h('h3', null, 'Жетон свойства'), h('div', { class: 'token' }, h(Txt, { cls: '', text: t.text }))) : null,
    h('h3', null, `Постоянки в игре (${p.permanents.length})`),
    h(Grid, { cards: p.permanents, keyPrefix: 'pi', empty: 'нет' }),
    p.inPlay.length ? h(Fragment, null, h('h3', null, `Сыграно в этот ход (${p.inPlay.length})`), h(Grid, { cards: p.inPlay, keyPrefix: 'pi' })) : null,
    h('h3', null, `Жетоны дохлых колдунов (${p.ddk.length})`),
    p.ddk.length ? p.ddk.map((id) => {
      const d = DB.ddk[id];
      return h('div', { class: 'token' }, h(Txt, { cls: '', text: d?.text || 'Без текста' }), ' ', h('span', { class: 'sub' }, `(${d ? ddkPenalty(d) : RULES.ddkBasePenalty} ПО)`));
    }) : h(Hint, null, 'нет'),
    h('h3', null, `Сброс — открытая информация (${p.discard.length})`),
    h(Grid, { cards: p.discard, keyPrefix: 'pi' }),
    // состав колоды: у себя — колода (рука и так на виду), у соперника — вместе с рукой (OpponentClosed)
    isMe
      ? h(Fragment, null,
          h('h3', null, `Колода (${n})`),
          h(Known, { p, keyPrefix: 'pik' }),
          p.deck.length
            ? h(Fragment, null,
                h(Hint, null, 'Порядок карт неизвестен — только состав (от большей стоимости к меньшей).'),
                h('div', { class: 'grid' }, byCostDesc(p.deck).map((c) => cardV({ inst: c }, { cls: 'sm', key: 'pi:' + c.iid, zone: 'view' }))))
            : h(Hint, null, 'пусто'))
      : h(OpponentClosed, { p, n }),
    h(Actions, null)));
}

/** Справка «что такое чипсины» — по правилам (стр. 15) */
export function chipsInfoV(p: Player): JSX.Element {
  return overlayV(h(Fragment, null,
    h('h2', null, h(ChipIc, null), ` Чипсины: ${p.chips}`),
    h('div', { class: 'cardtext' },
      `Жетоны-деньги. Очков не дают, копятся без ограничений (пока не кончился общий запас).

Откуда берутся: главный приз Крутагидона (+1 в конце каждого твоего хода), эффекты карт и беспределов, чипсины, лежащие на картах барахолки, — их забирает покупатель.

Куда тратятся: при покупке легенды 1 чипсина = 1 мощь (обычные карты — только за мощь); оплата эффектов карт и жетонов («потрать чипсину — …»).`),
    h(Actions, null)));
}

/** Имя жетона свойства по ключу — короткий ярлык для списка правок (полный текст правки идёт рядом) */
const TRAIT_LABEL: Record<string, string> = {
  prize: 'Главный приз на старте', act6: '☀ Чипсина за сокровища/твари', act10: '☀ Карта с колоды врага',
  beastTop: 'Тварь на верх колоды', treasureDiscount: 'Скидка на сокровища', wizardChip: 'Чипсина за волшебника',
  permChip: 'Чипсина за постоянку', hrenalochka: 'Палочки бьют сильнее', spellHand: 'Предел руки за заклинания',
  threeFamiliars: 'Три фамильяра',
};

/** Справка режима ребаланса: все правки по разделам нерфы/баффы/переработка (кнопка «что изменено» на стартовом экране) */
export function rebalanceInfoV(): JSX.Element {
  const name = (id: string): string => DB.cards[id]?.name ?? id;
  const rows: Record<'nerf' | 'buff' | 'rework', { id: string; what: string; weight: number }[]> = { nerf: [], buff: [], rework: [] };
  for (const [id, p] of Object.entries(REBALANCE)) {
    const from = baseCost(id);
    if (from == null || p.cost == null || from === p.cost) continue;
    rows[p.cost > from ? 'nerf' : 'buff'].push({ id, what: `цена ${from} → ${p.cost}`, weight: Math.abs(p.cost - from) });
  }
  for (const [id, note] of Object.entries(REBALANCE_NOTES)) rows[note.kind].push({ id, what: note.note, weight: 0 });
  // жетоны свойств: у них нет цены и плашки карты — отдельным списком под разделами карт
  const traitRows = Object.entries(REBALANCE_TRAIT_NOTES).map(([key, note]) => ({ key, ...note! }));
  // одна строка на правку: плашка карты (тап — полное описание с картинкой) + пояснение
  const list = (kind: 'nerf' | 'buff' | 'rework'): V =>
    h(Fragment, null, rows[kind].length
      ? rows[kind]
          .sort((a, b) => b.weight - a.weight || name(a.id).localeCompare(name(b.id)))
          .map((x) => h('div', { class: 'row nowrap' }, h(CardChipV, { cid: x.id }), h('span', { class: 'sub' }, x.what)))
      : h(Hint, null, 'нет'));
  return overlayV(h(Fragment, null,
    h('h2', null, '⚖️ Режим ребаланса'),
    h('div', { class: 'cardtext' }, 'Отдельный режим партии: цены и эффекты самых несбалансированных карт и жетонов свойств правлены по массовым симуляциям. В партии правленая карта показывает уже правленые цену и текст и помечена значком ⚖ — сравнить с обычными правилами можно, увеличив карту. Здесь перечислено, что именно изменено.'),
    h('h3', null, 'Нерфы'), list('nerf'),
    h('h3', null, 'Баффы'), list('buff'),
    h('h3', null, 'Переработка'), list('rework'),
    traitRows.length ? h(Fragment, null,
      h('h3', null, 'Жетоны свойств'),
      traitRows.map((t) => h('div', { class: 'row nowrap' },
        h('span', { class: 'token-chip' }, `⚖ ${TRAIT_LABEL[t.key] ?? t.key}`),
        h('span', { class: 'sub' }, `${t.kind === 'nerf' ? 'нерф' : t.kind === 'buff' ? 'бафф' : 'переработка'}: ${t.note}`)))) : null,
    h(Actions, null)));
}

/** Мутаторы этой партии: что изменено в правилах (и метка сида дня) */
export function mutatorInfoV(G: GameState): JSX.Element {
  const defs = mutatorDefs(G);
  return overlayV(h(Fragment, null,
    h('h2', null, G.daily ? '📅 Сид дня' : '🎲 Мутаторы партии'),
    G.daily ? h(Sub, null, `${fmtDate(G.daily)} · один стол на всех, одна попытка в день`) : null,
    defs.length
      ? defs.map((m) => h('div', { class: 'mut-def' }, h('div', { class: 'mut-name' }, `${m.emoji} ${m.name}`), h('div', { class: 'cardtext' }, m.desc)))
      : h('div', { class: 'cardtext' }, 'Классика: обычные правила без мутаторов.'),
    h(Actions, null)));
}

export function endTurnWarningV(power: number, buys: readonly string[]): JSX.Element {
  return overlayV(h(Fragment, null,
    h('h2', null, `⚡ Осталась мощь: ${power}`),
    h('div', { class: 'cardtext' }, `Ещё можно купить:\n${buys.map((b) => '· ' + b).join('\n')}`),
    h('div', { class: 'actions' },
      h(Btn, { cls: 'primary', c: { k: 'goBuy' } }, 'К барахолке'),
      h(Btn, { cls: 'danger', c: { k: 'endConfirm' } }, 'Всё равно закончить'))));
}

/** Новая партия поверх незаконченной: та пропадёт — спросить, а не затирать молча (плитка «Против ботов» — одно касание) */
export function replaceSavedV(names: readonly string[], quick: boolean, boss?: BossId): JSX.Element {
  return overlayV(h(Fragment, null,
    h('h2', null, 'Начать новую партию?'),
    h('div', { class: 'cardtext' }, `На этом телефоне есть незаконченная партия (${names.join(', ')}). Новая её заменит.`),
    h('div', { class: 'actions' },
      h(Btn, { cls: 'primary', c: { k: 'continue' } }, 'Продолжить старую'),
      h(Btn, { cls: 'danger', id: 'btn-replace-saved', c: { k: 'newConfirm', quick, ...(boss ? { boss } : {}) } }, 'Начать новую'))));
}

// ---------- уведомления и что нового ----------
/** Давность по-человечески: «только что», «5 мин назад», «3 ч назад», дата */
export function ago(at: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return 'только что';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} мин назад`;
  const hh = Math.round(m / 60);
  if (hh < 24) return `${hh} ч назад`;
  const d = new Date(at);
  return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export interface InboxOpts {
  /** новости об обновлениях включены */
  news: boolean;
  /** разрешение на системные уведомления браузера */
  sys: SysPermission;
  now?: number;
}

/** Ящик уведомлений: приглашения (кнопка «Войти»), новости («Что нового»), настройки новостей и системных уведомлений */
export function inboxV(items: readonly InboxItem[], { news, sys, now = Date.now() }: InboxOpts): JSX.Element {
  return overlayV(h(Fragment, null,
    h('h2', null, '🔔 Уведомления'),
    items.length
      ? items.map((i) => h('div', { class: cx('inbox-item', !i.read && 'unread', `inbox-${i.kind}`), 'data-id': i.id },
          h('div', { class: 'inbox-head' }, h('b', null, i.title), h('span', { class: 'sub' }, ago(i.at, now))),
          h('div', { class: 'inbox-text' }, i.text),
          h('div', { class: 'row mt' },
            i.kind === 'invite' && i.code ? h(Btn, { cls: 'primary', c: { k: 'joinLobby', code: i.code } }, `Войти в комнату ${i.code}`) : null,
            i.kind === 'news' ? h(Btn, { c: { k: 'changelog' } }, 'Что нового') : null,
            i.kind === 'corr' && i.code ? h(Btn, { cls: 'primary', c: { k: 'corrGo', code: i.code } }, 'Открыть партию') : null,
            i.kind === 'ach' ? h(Btn, { c: { k: 'profile' } }, '👤 Мои ачивки') : null,
            h(Btn, { cls: 'ghost', c: { k: 'inboxRemove', id: i.id } }, 'Убрать'))))
      : h(Hint, null, 'Пока пусто. Сюда приходят приглашения в игру, новые ачивки и новости об обновлениях.'),
    h('h3', null, 'Настройки'),
    h('label', { class: 'row nowrap' },
      h('input', { type: 'checkbox', id: 'cb-news', checked: news, ...cmdProps({ k: 'newsToggle' }) }),
      ' Сообщать об обновлениях игры'),
    sys === 'default' ? h(Btn, { cls: 'mt', id: 'btn-sys-notify', c: { k: 'sysNotify' } }, '📣 Разрешить системные уведомления') : null,
    sys === 'granted' ? h(Hint, null, 'Системные уведомления разрешены: приглашение придёт, даже если игра не на экране.') : null,
    sys === 'denied' ? h(Hint, null, 'Системные уведомления запрещены в настройках браузера — приглашения видны только в игре.') : null,
    h('div', { class: 'actions' }, items.length ? h(Btn, { cls: 'ghost', c: { k: 'inboxClear' } }, 'Очистить') : null, h(Close, null))));
}

/** Что нового: записи ченджлога, свежие первыми */
export function changelogV(): JSX.Element {
  return overlayV(h(Fragment, null,
    h('h2', null, '📋 Что нового'),
    CHANGELOG.map((e) => h('div', { class: 'changelog-entry' },
      h('h3', null, `${fmtDate(e.date)} — ${e.title}`),
      h('ul', null, e.items.map((s) => h('li', null, s))))),
    h(Actions, null)));
}

// ---------- профиль: места в рейтингах, поражения, имя и связанные устройства ----------

/** Загружаемый с сервера профиль игрока (GET /api/player); при листании поражений прежняя страница видна до ответа */
export interface PlayerLoad {
  data: PlayerProfile | null;
  loading: boolean;
  /** не загрузился: нет сети или игрока */
  error: boolean;
  /** в разделе ачивок видны все (и ещё не полученные), а не только полученные */
  achAll?: boolean;
}

/** Состояние окна своего профиля (src/ui/profile.ts) */
export interface ProfileState {
  /** вкладка: партии (места и поражения) или устройства (связывание) */
  tab: 'games' | 'devices';
  player: PlayerLoad;
  info: AccountInfo | null;
  loading: boolean;
  error: string;
  /** выданный код связывания: код, QR ссылки (svg), когда истекает (мс) */
  code: { code: string; qr: string; expires: number } | null;
  /** открыт ввод кода со второго устройства */
  entering: boolean;
  redeem: string;
  /** спрашиваем, точно ли отвязать это устройство */
  confirmLeave: boolean;
  /** только что связали — показать «готово» */
  linked: boolean;
}

const dayMonth = (sec: number): string => new Date(sec * 1000).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
const shortDate = (sec: number): string => new Date(sec * 1000).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }).replace('.', '');

/** «против сложных ботов» */
const LEVEL_GEN: Record<BotLevel, string> = { easy: 'лёгких', normal: 'средних', hard: 'сложных', cheat: 'нечестных' };
const againstText = (cat: 'bots' | 'people', level?: RatingLevel): string =>
  cat === 'people' || !level ? 'Против людей' : level === 'boss' ? 'Против боссов' : level === 'duel' ? 'Дуэли с боссами' : level === 'mut' ? 'С мутаторами' : `Против ${LEVEL_GEN[level]} ботов`;
const againstIcon = (cat: 'bots' | 'people', level?: RatingLevel): string =>
  cat === 'people' || !level ? '🌐' : level === 'boss' ? '👑' : level === 'duel' ? '🥊' : level === 'mut' ? '🎲' : LEVEL_INFO[level].emoji;
const placeText = (place: number, of: number): string => `${place + 1}-е место из ${of}`;

/** Места игрока в номинациях: строка на номинацию — как в таблице рейтинга */
function BoardsV({ p }: { p: PlayerProfile }): V {
  if (!p.boards.length) {
    return h(Hint, { cls: 'hint pf-empty' }, p.me
      ? 'Тебя пока нет в рейтингах: сыграй один против ботов или онлайн с людьми.'
      : 'Пока нет партий в рейтингах.');
  }
  return h('div', { class: 'lb-list pf-boards' }, p.boards.map((b) => {
    const people = b.cat === 'people';
    return h('div', { class: cx('lb-row', b.rank <= 3 && `top${b.rank}`) },
      h('span', { class: 'lb-rank' }, b.rank <= 3 ? MEDAL[b.rank - 1] : b.rank),
      h('div', { class: 'lb-main' },
        h('div', { class: 'lb-name' }, `${againstIcon(b.cat, b.level)} ${againstText(b.cat, b.level)}`),
        h('div', { class: 'sub' }, `${b.rank}-е из ${b.total} · ${gamesWord(b.games)} · рекорд ${b.best}★`)),
      h('div', { class: 'lb-val' },
        h('b', null, people ? b.rating ?? '—' : b.wins),
        h('span', { class: 'sub' }, people ? 'рейтинг' : winsWord(b.wins))));
  }));
}

/** Партия в списке: когда, против кого, место, очки и кто победил; тап — плеер. best — лучшая партия номинации:
 *  очки — первыми */
function GameRowV({ l, owner, best = false }: { l: PlayedGame; owner: 'me' | 'other'; best?: boolean }): V {
  const winner = l.table.find((x) => x.place === 0);
  const result = l.place === 0 ? '🏆 победа' : winner ? `🏆 ${winner.name} ${winner.score}★` : '';
  const sub = best
    ? [`${l.score}★`, placeText(l.place, l.players), result]
    : [placeText(l.place, l.players), `${l.score}★`, result];
  return h(Btn, { cls: cx('game-row', best && 'best'), c: { k: 'playedGame', game: l.game, owner } },
    h('span', { class: 'gr-ic' }, againstIcon(l.cat, l.level)),
    h('div', { class: 'lb-main' },
      h('div', { class: 'gr-head' }, h('b', null, againstText(l.cat, l.level)), h('span', { class: 'sub' }, shortDate(l.at))),
      h('div', { class: 'sub' }, sub.filter(Boolean).join(' · '))),
    h('span', { class: 'gr-go' }, '›'));
}

/** Листалка поражений: «новее» / «старее» и какие из скольких на экране */
function LossPager({ p, owner, load }: { p: PlayerProfile; owner: 'me' | 'other'; load: PlayerLoad }): V {
  if (p.lossCount <= p.lossPage) return null;
  const loading = load.loading;
  const from = p.lossOffset;
  const to = Math.min(from + p.losses.length, p.lossCount);
  return h('div', { class: 'pager' },
    h(Btn, { cls: 'ghost', id: 'btn-loss-newer', c: { k: 'lossPage', owner, offset: Math.max(0, from - p.lossPage) }, disabled: loading || from === 0 }, '‹ Новее'),
    h('span', { class: 'sub pager-pos' }, loading ? 'Загрузка…' : load.error ? 'нет связи' : `${from + 1}–${to} из ${p.lossCount}`),
    h(Btn, { cls: 'ghost', id: 'btn-loss-older', c: { k: 'lossPage', owner, offset: from + p.lossPage }, disabled: loading || to >= p.lossCount }, 'Старее ›'));
}

/** Коллекция боссов: десять портретов — побеждённые цветные с числом побед, встреченные — тусклые, прочие — серые */
export function BossCollectionV({ stats }: { stats: readonly BossStat[] }): V {
  const by = new Map(stats.map((s) => [s.id, s]));
  const beaten = stats.filter((s) => s.wins > 0).length;
  return h(Fragment, null,
    h('h3', null, `👑 Боссы · побеждено ${beaten} из ${BOSS_IDS.length}`),
    h('div', { class: 'boss-coll' }, BOSS_IDS.map((id) => {
      const s = by.get(id);
      const state = s?.wins ? 'won' : s ? 'met' : 'none';
      return h('div', { class: cx('bc-item', state), title: `${BOSSES[id].name}: ${s ? `побед ${s.wins} из ${s.games}` : 'ещё не встречались'}` },
        h('img', { src: DB.cards[BOSSES[id].familiar]?.art ?? '', alt: '' }),
        h('span', { class: 'bc-name' }, BOSSES[id].short),
        h('span', { class: 'bc-n' }, s ? `${s.wins}/${s.games}` : '—'));
    })));
}

/** Все мутаторы с описаниями (справка рядом с выбором мутаторов) */
export function mutatorsHelpV(): JSX.Element {
  return overlayV(h(Fragment, null,
    h('h2', null, '🎲 Мутаторы'),
    h('div', { class: 'cardtext' }, 'Мутатор меняет правила на одну партию. Можно включить несколько сразу. Против сложных ботов и сильнее партии с мутаторами идут в свою номинацию рейтинга «🎲 с мутаторами», против лёгких и средних — в таблицу своего уровня.'),
    MUTATOR_IDS.map((id) => h('div', { class: 'mut-def' }, h('div', { class: 'mut-name' }, `${MUTATORS[id].emoji} ${MUTATORS[id].name}`), h('div', { class: 'cardtext' }, MUTATORS[id].desc))),
    h(Actions, null)));
}

/** Тело профиля игрока: итоги, места в номинациях, лучшие партии, проигранные партии постранично */
function PlayerBody({ load, owner }: { load: PlayerLoad; owner: 'me' | 'other' }): V {
  const p = load.data;
  if (!p) return h(Hint, { cls: 'hint pf-empty' }, load.loading ? 'Загрузка…' : 'Не удалось загрузить — нет связи с сервером.');
  // нет партий в рейтингах — нечего и перечислять: одна подсказка вместо пустых разделов
  if (!p.games) return h(BoardsV, { p });
  return h(Fragment, null,
    h(Sub, { cls: 'sub pf-sum' }, `${gamesWord(p.games)} в рейтингах · ${p.wins} ${winsWord(p.wins)}`),
    h(BoardsV, { p }),
    h(BossCollectionV, { stats: p.bosses ?? [] }),
    h(AchSectionV, { p, owner, all: !!load.achAll }),
    h('h3', null, 'Лучшие партии'),
    h(Hint, null, 'Больше всего очков в каждой номинации. Тапни партию — она проиграется на столе ход за ходом, в конце — разбор решений.'),
    h('div', { class: 'game-list' }, p.best.map((l) => h(GameRowV, { l, owner, best: true }))),
    h('h3', { id: 'losses-head' }, `Проигранные партии${p.lossCount ? ` (${p.lossCount})` : ''}`),
    p.losses.length
      ? h(Fragment, null,
          h('div', { class: 'game-list', id: 'loss-list' }, p.losses.map((l) => h(GameRowV, { l, owner }))),
          h(LossPager, { p, owner, load }))
      : h(Hint, null, 'Ни одного поражения — так держать!'));
}

/** Профиль другого игрока (тап по строке рейтинга) */
export function playerV(load: PlayerLoad): JSX.Element {
  return overlayV(h(Fragment, null,
    h('h2', null, `👤 ${load.data?.name ?? 'Игрок'}`),
    h(PlayerBody, { load, owner: 'other' }),
    h(Actions, null)), '', 'profile');
}

/** Конец партии в плеере записи (src/ui/replay.ts): итоговая таблица и разбор решений игрока из профиля */
export interface ReplayEnd {
  game: PlayedGame;
  /** чья партия: его строка итога подсвечена, его решения разбираются */
  who: string;
  /** это свой профиль */
  me: boolean;
  /** место игрока за столом (pid) */
  seat: PlayerId;
  review: ReviewItem[] | null;
  reviewBusy: boolean;
  error: string;
}

export function replayEndV(v: ReplayEnd): JSX.Element {
  const l = v.game;
  return overlayV(h(Fragment, null,
    h('h2', null, '🏁 Партия окончена'),
    h(Sub, null, `${againstIcon(l.cat, l.level)} ${againstText(l.cat, l.level)} · ${dayMonth(l.at)}`),
    h('div', { class: 'lb-list' }, l.table.map((x) => h('div', { class: cx('lb-row', x.place === l.place && 'me', x.place < 3 && `top${x.place + 1}`) },
      h('span', { class: 'lb-rank' }, x.place < 3 ? MEDAL[x.place] : x.place + 1),
      h('div', { class: 'lb-main' },
        h('div', { class: 'lb-name' }, x.place === l.place ? v.who : x.name, x.place === l.place && v.me ? h('span', { class: 'lb-you' }, ' (ты)') : null),
        x.bot && x.level ? h('div', { class: 'sub' }, `${LEVEL_INFO[x.level].emoji} бот, ${LEVEL_INFO[x.level].label}`) : null),
      h('div', { class: 'lb-val' }, h('b', null, x.score), h('span', { class: 'sub' }, '★'))))),
    h('h3', null, '🔍 Разбор решений'),
    v.review
      ? h(Fragment, null, h(Sub, null, REVIEW_SUB), h(ReviewOf, { name: v.who, items: v.review, jump: true }))
      : v.reviewBusy
        ? h(Hint, null, 'Разбираю решения — это несколько секунд…')
        : h(Fragment, null,
            h(Hint, null, `Где ${v.me ? 'ты потерял' : `${v.who} потерял(а)`} очки: каждое решение сравнивается с ходом бота.`),
            h(Btn, { id: 'btn-replay-review', c: { k: 'replay', op: 'review' } }, 'Разобрать партию')),
    v.error ? h('div', { class: 'hint err-text' }, v.error) : null,
    h('div', { class: 'actions' },
      h(Btn, { id: 'btn-replay-restart', c: { k: 'replay', op: 'restart' } }, '↺ Сначала'),
      h(Btn, { cls: 'ghost', id: 'btn-replay-exit', c: { k: 'replay', op: 'exit' } }, 'Выйти'),
      h(Close, null))), '', 'profile');
}
const hhmm = (ms: number): string => new Date(ms).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
/** K7P2QX → K7P 2QX: читать и перепечатывать легче */
const spacedCode = (c: string): string => (c.length === 6 ? `${c.slice(0, 3)} ${c.slice(3)}` : c);

/**
 * Профиль → «Устройства» → «Уведомления»: на каких устройствах аккаунта включены уведомления игры по переписке
 * (будят все сразу); своё — включить, выключить, проверить; чужое — выключить.
 */
function PushDevicesV({ push }: { push: PushState }): V {
  const list = push.devices ?? [];
  const mine = list.some((d) => d.me);
  return h(Fragment, null,
    h('h3', null, '🔔 Уведомления'),
    h(Hint, null, 'Партия по переписке будит уведомлением, когда до тебя доходит ход, — на всех этих устройствах сразу.'),
    list.length
      ? h('div', { class: 'devices' }, list.map((d) => h('div', { class: cx('device', d.me && 'me') },
          h('span', { class: 'dev-ic' }, /iPhone|iPad|Android/.test(d.label) ? '📱' : '💻'),
          h('div', { class: 'dev-main' }, h('b', null, d.label || 'Устройство'),
            h(Sub, null, `${d.me ? 'это устройство · ' : ''}включены с ${dayMonth(d.since)}${d.okAt ? `, последнее — ${dayMonth(d.okAt)}` : ''}`)),
          h(Btn, { cls: 'ghost small', id: `btn-push-off-${d.id}`, c: { k: 'pushRemove', id: d.id }, disabled: push.busy }, 'Выключить'))))
      : h(Hint, null, push.devices == null ? (push.busy ? 'Загрузка…' : 'Не удалось загрузить.') : 'Уведомления не включены ни на одном устройстве.'),
    mine
      ? h(Btn, { cls: 'link', id: 'btn-push-test', c: { k: 'pushTest' }, disabled: push.busy }, 'Проверить на этом устройстве')
      : push.status === 'off'
        ? h(Btn, { id: 'btn-push-enable', c: { k: 'pushEnable' }, disabled: push.busy }, '🔔 Включить на этом устройстве')
        : push.status === 'denied'
          ? h(Hint, null, 'На этом устройстве уведомления запрещены в настройках браузера.')
          : push.status === 'iosBrowser'
            ? h(Hint, null, 'На iPhone уведомления приходят только приложению на экране «Домой».')
            : push.status === 'unsupported' ? h(Hint, null, 'Этот браузер не умеет системные уведомления.') : null,
    push.error ? h('div', { class: 'hint err-text', id: 'push-error' }, push.error) : null);
}

export function profileV(p: ProfileState, name: string, online: boolean, push: PushState | null = null): JSX.Element {
  const devices = p.info?.devices ?? [];
  let link: V;
  if (p.confirmLeave) {
    link = h('div', { class: 'col' },
      h(Hint, null, 'Отвязать это устройство? Его партии уйдут вместе с ним в отдельную строку рейтинга, остальные устройства останутся вместе.'),
      h('div', { class: 'row' },
        h(Btn, { cls: 'danger', id: 'btn-link-leave-yes', c: { k: 'linkLeave', confirmed: true }, disabled: p.loading }, 'Отвязать'),
        h(Btn, { cls: 'ghost', c: { k: 'linkCancel' } }, 'Отмена')));
  } else if (p.code) {
    link = h('div', { class: 'link-show' },
      h('div', { class: 'link-code', id: 'link-code-value' }, spacedCode(p.code.code)),
      h(Raw, { cls: 'qr', html: p.code.qr }),
      h(Hint, null, `На втором устройстве открой «👤 Профиль → Ввести код» или наведи камеру на QR. Код действует до ${hhmm(p.code.expires)}.`),
      h(Btn, { cls: 'link', c: { k: 'linkCancel' } }, 'Скрыть код'));
  } else if (p.entering) {
    link = h('div', { class: 'col' },
      h('div', { class: 'join-row' },
        h('input', { type: 'text', id: 'link-code', placeholder: 'Код со второго устройства', value: p.redeem, maxlength: 9, autocomplete: 'off', style: 'text-transform:uppercase' }),
        h(Btn, { cls: 'primary', id: 'btn-link-redeem', c: { k: 'linkRedeem' }, disabled: p.loading }, 'Связать')),
      h(Hint, null, 'Партии этого устройства перейдут в аккаунт того, где показан код, — дальше они считаются вместе.'),
      h(Btn, { cls: 'link', c: { k: 'linkCancel' } }, 'Отмена'));
  } else {
    link = h('div', { class: 'row' },
      h(Btn, { id: 'btn-link-show', c: { k: 'linkShow' }, disabled: p.loading || !online }, '📲 Показать код'),
      h(Btn, { id: 'btn-link-enter', c: { k: 'linkEnter' }, disabled: p.loading || !online }, '⌨️ Ввести код'));
  }
  const tab = (t: ProfileState['tab'], label: string): V =>
    h(Btn, { cls: cx('seg-btn', p.tab === t && 'on'), id: `btn-pf-${t}`, c: { k: 'profileTab', tab: t } }, label);
  return overlayV(h(Fragment, null,
    h('h2', null, '👤 Профиль'),
    h('label', { class: 'field' }, h('span', { class: 'field-label' }, 'Твоё имя'),
      h('input', { type: 'text', id: 'online-name', placeholder: 'Как тебя звать', value: name, maxlength: 16 })),
    h(Hint, null, 'Так тебя видят в рейтингах и в онлайне.'),
    h('div', { class: 'seg seg-2x2 pf-tabs' }, tab('games', '📊 Партии'), tab('devices', '📱 Устройства')),
    p.tab === 'games' ? h(PlayerBody, { load: p.player, owner: 'me' }) : h(Fragment, null,
    h('h3', null, 'Устройства'),
    devices.length
      ? h('div', { class: 'devices' }, devices.map((d) => h('div', { class: cx('device', d.me && 'me') },
          h('span', { class: 'dev-ic' }, /iPhone|iPad|Android/.test(d.label) ? '📱' : '💻'),
          h('div', { class: 'dev-main' }, h('b', null, d.label), h(Sub, null, d.me ? 'это устройство' : `с ${dayMonth(d.since)}, заходило ${dayMonth(d.seen)}`)))))
      : h(Hint, null, p.loading ? 'Загрузка…' : online ? 'Не удалось загрузить.' : 'Нет связи с сервером.'),
    p.linked ? h('div', { class: 'hint ok-text' }, '✅ Готово: устройства связаны — партии со всех идут в рейтинг одной строкой.') : null,
    h('h3', null, 'Связать с другим устройством'),
    p.code || p.entering || p.confirmLeave ? null
      : h(Hint, null, 'Играешь с телефона и компьютера? Свяжи их: партии со всех устройств пойдут в рейтинг одной строкой, а если на одном очистится браузер, результаты останутся на другом.'),
    link,
    p.error ? h('div', { class: 'hint err-text', id: 'profile-error' }, p.error) : null,
    devices.length > 1 && !p.confirmLeave && !p.code && !p.entering
      ? h(Btn, { cls: 'link', id: 'btn-link-leave', c: { k: 'linkLeave', confirmed: false } }, 'Отвязать это устройство')
      : null,
    push ? h(PushDevicesV, { push }) : null),
    h(Actions, null)), '', 'profile');
}

// ---------- строковые обёртки (тесты, строковые потребители) ----------
export const profileHtml = (...a: Parameters<typeof profileV>): string => toString(profileV(...a));
export const playerHtml = (...a: Parameters<typeof playerV>): string => toString(playerV(...a));
export const replayEndHtml = (...a: Parameters<typeof replayEndV>): string => toString(replayEndV(...a));
export const inboxHtml = (...a: Parameters<typeof inboxV>): string => toString(inboxV(...a));
export const changelogHtml = (): string => toString(changelogV());
export const zoomHtml = (...a: Parameters<typeof zoomV>): string => toString(zoomV(...a));
export const cardInfoHtml = (...a: Parameters<typeof cardInfoV>): string => toString(cardInfoV(...a));
export const scoreHtml = (...a: Parameters<typeof scoreV>): string => toString(scoreV(...a));
export const tokenCardHtml = (...a: Parameters<typeof tokenCardV>): string => toString(tokenCardV(...a));
export const tokensHtml = (...a: Parameters<typeof tokensV>): string => toString(tokensV(...a));
export const pileHtml = (...a: Parameters<typeof pileV>): string => toString(pileV(...a));
export const logHtml = (...a: Parameters<typeof logV>): string => toString(logV(...a));
export const reviewHtml = (...a: Parameters<typeof reviewV>): string => toString(reviewV(...a));
export const installHintHtml = (...a: Parameters<typeof installHintV>): string => toString(installHintV(...a));
export const installNowHtml = (...a: Parameters<typeof installNowV>): string => toString(installNowV(...a));
export const installedHtml = (...a: Parameters<typeof installedV>): string => toString(installedV(...a));
export const menuHtml = (...a: Parameters<typeof menuV>): string => toString(menuV(...a));
export const manualHtml = (...a: Parameters<typeof manualV>): string => toString(manualV(...a));
export const trashHtml = (...a: Parameters<typeof trashV>): string => toString(trashV(...a));
export const rulesHtml = (...a: Parameters<typeof rulesV>): string => toString(rulesV(...a));
export const playerInfoHtml = (...a: Parameters<typeof playerInfoV>): string => toString(playerInfoV(...a));
export const chipsInfoHtml = (...a: Parameters<typeof chipsInfoV>): string => toString(chipsInfoV(...a));
export const mutatorInfoHtml = (...a: Parameters<typeof mutatorInfoV>): string => toString(mutatorInfoV(...a));
export const mutatorsHelpHtml = (): string => toString(mutatorsHelpV());
export const rebalanceInfoHtml = (...a: Parameters<typeof rebalanceInfoV>): string => toString(rebalanceInfoV(...a));
export const endTurnWarningHtml = (...a: Parameters<typeof endTurnWarningV>): string => toString(endTurnWarningV(...a));
